/**
 * ClearableInput
 *
 * Small input wrapper that exposes a controlled `value`/`onChange` API and
 * renders a clickable "X" icon to clear the field. Styling mirrors the
 * base `Input` component and keeps semantics/accessibility intact.
 */
import { X } from 'lucide-react';
import * as React from 'react';

import { Input, InputGroup } from '@/components/ui/catalyst/input';

interface ClearableInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  value: string;
  onChange: (value: string) => void;
}

export const ClearableInput: React.FC<ClearableInputProps> = ({
  value,
  onChange,
  className,
  ...props
}) => {
  return (
    <div className="relative">
      <InputGroup>
        <Input
          {...props}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`${className || ''}`}
        />
        {/* Invisible slot icon to reserve padding with InputGroup */}
        {value && (
          <span data-slot="icon" aria-hidden className="opacity-0">
            <X className="h-4 w-4" />
          </span>
        )}
      </InputGroup>
      {value && (
        <button
          type="button"
          aria-label="Clear input"
          className="absolute right-3 sm:right-2.5 top-1/2 -translate-y-1/2 h-7 w-7 grid place-items-center rounded hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50"
          onClick={() => onChange('')}
        >
          <X className="h-4 w-4 text-gray-500" />
        </button>
      )}
    </div>
  );
};
